import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './SinksLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  projectType: string
}

function SinksLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          projectType: data.projectType,
          _subject: 'Lab Sinks Quote Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Laboratory Sinks',
    description:
      'Chemical-resistant laboratory sinks. Epoxy resin, polypropylene, stainless steel, and specialty materials for your lab needs.',
    brand: { '@type': 'Brand', name: 'Laboratory Specialists' },
    offers: { '@type': 'Offer', priceCurrency: 'USD', availability: 'https://schema.org/UponRequest' },
  }

  return (
    <div className="sinks-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero: headline + sub-headline + proof + CTA */}
      <section className="sinks-hero">
        <div className="sinks-hero-bg" aria-hidden />
        <div className="container sinks-hero-inner">
          <h1 className="sinks-hero-title">
            Lab Sinks That Stand Up to Your Toughest Chemicals
          </h1>
          <p className="sinks-hero-subtitle">
            Chemical-resistant sinks in epoxy resin, polypropylene, stainless steel, and specialty materials. We'll help you choose the right sink for your lab's specific needs.
          </p>
          <div className="sinks-hero-cta">
            <button type="button" className="sinks-cta-button" onClick={scrollToForm}>
              Get Your Free Quote
            </button>
            <p className="sinks-hero-cta-note">Custom quote in 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Three objections (below the fold) */}
      <section className="sinks-objections">
        <div className="container">
          <ul className="sinks-bullets">
            <li>
              <strong>Not sure which material is right?</strong> We'll recommend epoxy resin, polypropylene, stainless steel, or specialty sinks based on your chemicals, durability requirements, and budget.
            </li>
            <li>
              <strong>Worried about proper sizing and plumbing integration?</strong> We design and install sinks to fit your lab layout, accommodate your workflow, and integrate seamlessly with existing plumbing.
            </li>
            <li>
              <strong>Need one partner for the whole job?</strong> We handle sink selection, plumbing parts, installation, and testing—one quote, one timeline.
            </li>
          </ul>
        </div>
      </section>

      {/* Quote form - primary conversion */}
      <section className="sinks-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="sinks-form-card">
            <h2 className="sinks-form-title">Get Your Custom Quote</h2>
            <p className="sinks-form-lead-magnet">
              Get a custom quote for chemical-resistant lab sinks—no obligation.
            </p>

            {isSubmitted ? (
              <div className="sinks-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll email or call with your quote and next steps.</p>
              </div>
            ) : (
              <form className="sinks-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="sinks-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}
                <div className="sinks-form-group">
                  <label htmlFor="sinks-name">
                    Full Name <span className="sinks-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="sinks-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="sinks-error" role="alert">
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className="sinks-form-group">
                  <label htmlFor="sinks-email">
                    Email <span className="sinks-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="sinks-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="sinks-error" role="alert">
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <div className="sinks-form-group">
                  <label htmlFor="sinks-phone">
                    Phone <span className="sinks-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="sinks-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="sinks-error" role="alert">
                      {errors.phone.message}
                    </span>
                  )}
                </div>

                <div className="sinks-form-group">
                  <label htmlFor="sinks-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="sinks-company"
                    {...register('company')}
                    placeholder="Your lab or company name"
                  />
                </div>

                <div className="sinks-form-group">
                  <label htmlFor="sinks-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="sinks-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="sinks-form-group">
                  <label htmlFor="sinks-projectType">
                    What do you need? <span className="sinks-required">*</span>
                  </label>
                  <select
                    id="sinks-projectType"
                    {...register('projectType', { required: 'Please select an option' })}
                    aria-invalid={errors.projectType ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="epoxy-resin-sink">Epoxy resin sink</option>
                    <option value="polypropylene-sink">Polypropylene sink</option>
                    <option value="stainless-steel-sink">Stainless steel sink</option>
                    <option value="specialty-sink">Specialty sink material</option>
                    <option value="replacement">Sink replacement</option>
                    <option value="new-installation">New sink installation</option>
                    <option value="plumbing-parts">Plumbing parts only</option>
                    <option value="not-sure">Not sure yet—need guidance</option>
                  </select>
                  {errors.projectType && (
                    <span className="sinks-error" role="alert">
                      {errors.projectType.message}
                    </span>
                  )}
                </div>

                <p className="sinks-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="sinks-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Get My Quote'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="sinks-legal">
        <div className="container">
          <p className="sinks-legal-text">
            Laboratory Specialists. Lab equipment installation and sink systems. Available in various materials and configurations.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default SinksLandingPage
